import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
} from 'react';
import axios, { AxiosInstance } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';

export const ApiClientContext = createContext<AxiosInstance | undefined>(
  undefined
);

export const ApiClientProvider = ({ children }: PropsWithChildren<unknown>) => {
  const { getAccessTokenSilently } = useAuth0();

  const client = useMemo(() => {
    const instance = axios.create({
      baseURL: window.config.API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    instance.interceptors.request.use(async (config) => {
      const token = await getAccessTokenSilently();

      if (token) {
        // eslint-disable-next-line no-param-reassign
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    });

    return instance;
  }, [getAccessTokenSilently]);

  return (
    <ApiClientContext.Provider value={client}>
      {children}
    </ApiClientContext.Provider>
  );
};

export const useApiClient = () => {
  const client = useContext(ApiClientContext);

  if (!client) {
    throw new Error('useApiClient must be used within an ApiClientProvider');
  }

  return client;
};
